import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { clearDrafts } from '../lib/chatDrafts';
import { api, ApiError } from '../api/client';
import type { User } from '../api/types';
import { clearFilterPreferences } from '../lib/filterStorage';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateUser: (update: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const userQuery = useQuery({
    queryKey: ['current-user'],
    queryFn: api.currentUser,
    refetchInterval: 30000,
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 401) && failureCount < 2,
  });

  const login = useCallback(
    async (loginValue: string, password: string) => {
      const user = await api.login(loginValue, password);
      queryClient.setQueryData(['current-user'], user);
      return user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    const currentUser = queryClient.getQueryData<User>(['current-user']);
    try {
      await api.logout();
    } finally {
      clearDrafts();
      if (currentUser) clearFilterPreferences(currentUser.id);
      queryClient.setQueryData(['current-user'], null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'current-user' });
    }
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.fetchQuery({
      queryKey: ['current-user'],
      queryFn: api.currentUser,
      staleTime: 0,
    });
  }, [queryClient]);

  const updateUser = useCallback(
    (update: Partial<User>) => {
      queryClient.setQueryData<User | null>(['current-user'], (current) =>
        current ? { ...current, ...update } : current,
      );
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: userQuery.data ?? null,
      loading: userQuery.isPending,
      login,
      logout,
      refresh,
      updateUser,
    }),
    [login, logout, refresh, updateUser, userQuery.data, userQuery.isPending],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
